import { __rest, __assign } from 'tslib';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-form.css';

var FormContext = React.createContext({});
var UitkFormProvider = function (_a) {
    var children = _a.children, fieldChangeCallback = _a.fieldChangeCallback, hasRequiredFieldIndicator = _a.hasRequiredFieldIndicator;
    return (React.createElement(FormContext.Provider, { value: { fieldChangeCallback: fieldChangeCallback, hasRequiredFieldIndicator: hasRequiredFieldIndicator } }, children));
};
/**
 * Hook for invoking an available callback (if any) set in UitkForm.onFieldChange prop
 *
 * @param {name} name Speficies the name of form element (input, select, etc.)
 * used for reference data after a form is submitted
 * @param {function} transformer Callback for manipulating event or triggering effects.
 * Normally used for setting inner state in a component
 *
 * @returns Callback meant to be set on elements event handlers
 *
 * @example
 * const handleChange = useFormCallback('destination', (event) => { console.debug(event) })
 * <input
 * onChange={handleChange}>
 * />
 */
var useFormCallback = function (name, transformer) {
    var fieldChangeCallback = React.useContext(FormContext).fieldChangeCallback;
    if (!fieldChangeCallback) {
        throw new Error('fieldChangeCallback prop should be set in any UitkForm in the parent tree of a field using this hook');
    }
    return React.useCallback(function (event) {
        var transformedEvent = transformer(event);
        var _a = transformedEvent.currentTarget.value, value = _a === void 0 ? '' : _a;
        fieldChangeCallback({ name: name, value: value }, transformedEvent);
        return transformedEvent;
    }, [name, transformer, fieldChangeCallback]);
};

var getClassNameList$1 = classNames.bind(styles);
var UitkForm = React.forwardRef(function (props, ref) {
    var hasRequiredFieldIndicator = props.hasRequiredFieldIndicator, children = props.children, className = props.className, onFieldChange = props.onFieldChange, formProps = __rest(props, ["hasRequiredFieldIndicator", "children", "className", "onFieldChange"]);
    var baseClass = 'uitk-form';
    var wrapperClassNames = getClassNameList$1(baseClass, { 'has-required-indicator': hasRequiredFieldIndicator }, className);
    return (React.createElement(UitkFormProvider, { fieldChangeCallback: onFieldChange, hasRequiredFieldIndicator: hasRequiredFieldIndicator },
        React.createElement("form", __assign({ ref: ref }, formProps, { className: wrapperClassNames }), children)));
});
UitkForm.displayName = 'UitkForm';

var GroupedFieldsContext = React.createContext({});
var UitkGroupedFieldsProvider = function (_a) {
    var children = _a.children, hasRequiredGroupFieldIndicator = _a.hasRequiredGroupFieldIndicator;
    return (React.createElement(GroupedFieldsContext.Provider, { value: { hasRequiredGroupFieldIndicator: hasRequiredGroupFieldIndicator } }, children));
};

var getClassNameList = classNames.bind(styles);
var UitkGroupedFields = function (props) {
    var children = props.children, className = props.className, label = props.label, hasRequiredGroupFieldIndicator = props.hasRequiredGroupFieldIndicator, fieldSetProps = __rest(props, ["children", "className", "label", "hasRequiredGroupFieldIndicator"]);
    var hasIndicator = React.useContext(FormContext).hasRequiredFieldIndicator;
    var baseClass = 'uitk-form-grouped-fields';
    var wrapperClassNames = getClassNameList(baseClass, { 'has-required-indicator': hasIndicator && hasRequiredGroupFieldIndicator }, className);
    var legendClassName = getClassNameList('uitk-form-grouped-fields-label');
    var legend = React.createElement("legend", { className: legendClassName }, label);
    if (React.isValidElement(label)) {
        var labelText = React.Children.toArray(label.props.children).reduce(function (accumulator, child) {
            if (typeof child === 'string') {
                accumulator.push(child);
            }
            else if (typeof child === 'object') {
                accumulator.push(child.props.children);
            }
            return accumulator.join('');
        }, []);
        legend = React.cloneElement(label, {}, React.createElement("legend", { className: legendClassName }, labelText));
    }
    return (React.createElement(UitkGroupedFieldsProvider, { hasRequiredGroupFieldIndicator: hasRequiredGroupFieldIndicator },
        React.createElement("fieldset", __assign({}, fieldSetProps, { className: wrapperClassNames }),
            legend,
            children)));
};
UitkGroupedFields.displayName = 'UitkGroupedFields';

export { FormContext, GroupedFieldsContext, UitkForm, UitkFormProvider, UitkGroupedFields, useFormCallback };
